import { useState, useEffect } from 'react'

type Theme = 'light' | 'dark' | 'system'

const THEME_STORAGE_KEY = 'brandscope-theme'

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(() => {
    // Check localStorage first
    const stored = localStorage.getItem(THEME_STORAGE_KEY) as Theme
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      return stored
    }
    
    // Default to system preference
    return 'system'
  })

  // Function to get the actual theme (resolving 'system' to 'light' or 'dark')
  const getResolvedTheme = (): 'light' | 'dark' => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return theme
  }

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(getResolvedTheme)

  // Update theme class on document
  useEffect(() => {
    const root = document.documentElement
    const newResolvedTheme = getResolvedTheme()
    
    setResolvedTheme(newResolvedTheme)
    
    // Remove all theme classes
    root.classList.remove('light', 'dark')
    
    // Add current theme class
    root.classList.add(newResolvedTheme)
    
    // Store theme preference
    localStorage.setItem(THEME_STORAGE_KEY, theme)
  }, [theme])

  // Listen for system theme changes when using 'system' theme
  useEffect(() => {
    if (theme !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    const handleChange = () => {
      const newResolvedTheme = getResolvedTheme()
      setResolvedTheme(newResolvedTheme)
      
      const root = document.documentElement
      root.classList.remove('light', 'dark')
      root.classList.add(newResolvedTheme)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  const setLightTheme = () => setTheme('light')
  const setDarkTheme = () => setTheme('dark')
  const setSystemTheme = () => setTheme('system')

  const toggleTheme = () => {
    if (theme === 'system') {
      // If currently system, toggle to opposite of current resolved theme
      setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
    } else {
      // If explicitly set, toggle between light and dark
      setTheme(theme === 'dark' ? 'light' : 'dark')
    }
  }

  return {
    theme,
    resolvedTheme,
    setLightTheme,
    setDarkTheme,
    setSystemTheme,
    toggleTheme,
    isLight: resolvedTheme === 'light',
    isDark: resolvedTheme === 'dark',
    isSystem: theme === 'system'
  }
}